import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import { Animation } from '@travel/ui';
import { isEmptyArray } from '@travel/utils';

import Pagination from 'favoriteList/components/Pagination';
import { DEFAULT_LIMIT_PLAN } from 'favoriteList/constants/defaultValue';
import { ServiceType } from 'favoriteList/constants/favoriteLists';
import { getFilterObject, getQueryString } from 'favoriteList/constants/filters';
import paths from 'favoriteList/core/universalRouter/paths';
import useActionTimers from 'favoriteList/hooks/useActionTimers';
import { getRequestFilter, getRequestSort } from 'favoriteList/pages/FavoriteList/resolver';
import { pushLocation } from 'favoriteList/store/__router/actions';
import {
  addFavoriteToPlan,
  fetchMorePlanList,
  fetchPlanList,
  removeFavoriteToPlan,
} from 'favoriteList/store/providerPlan/actions';
import {
  getIsPlanFetching,
  getIsPlanFetchingMore,
  getPlanDataCount,
  getPlanList,
} from 'favoriteList/store/providerPlan/selectors';
import AccommodationPlanSkeleton from '../AccommodationPlanSkeleton';
import EmptyMessage from '../EmptyMessage';
import Popup from '../Popup';
import PlanDetailInformationCard from './PlanDetailInformationCard';
import ProviderInformationCard from './ProviderInformationCard';

import type { PlanListWithArrayIndex } from 'Plan-Types';
import type { Query } from 'ProviderPlan-Types';

import styles from './accommodationPlanList.module.scss';

type Props = {
  query: Query;
  isDomestic: boolean;
  isPlanList: boolean;
};

const AccommodationPlanList = (props: Props) => {
  const { query, isDomestic, isPlanList } = props;
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;
  const [removeItemCount, setRemoveItemCount] = useState(0);

  const planResponse = useSelector(getPlanList);
  const { body, status, statusMsg } = planResponse;
  const offset = body?.offset || 0;
  const limit = body?.limit || DEFAULT_LIMIT_PLAN;
  const isPlanFetchingMore = useSelector(getIsPlanFetchingMore);
  const isPlanFetching = useSelector(getIsPlanFetching);
  const total = useSelector(getPlanDataCount);
  const fadePlanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hidePlanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTotalDataEmpty = total === 0;
  const isCurrentPageDataEmpty = total - offset === 0;

  const refreshPlanList = () => {
    if (!isSP) {
      if (isCurrentPageDataEmpty) {
        const newOffset = Math.max(0, offset - limit);
        const path = isDomestic ? paths.domesticPlan.path : paths.overseasPlan.path;
        const queryString = getQueryString(query.f_sort || '', getFilterObject(query), newOffset);

        dispatch(
          pushLocation({
            pathname: path,
            search: queryString,
          }),
        );
      } else {
        dispatch(
          fetchPlanList({
            serviceType: isDomestic ? ServiceType.DOMESTIC : ServiceType.OVERSEAS_HOTEL,
            offset: offset,
            limit: limit,
            filters: getRequestFilter(query),
            sortOrder: getRequestSort(false, query),
            adultNum: 1,
            roomNum: 1,
          }),
        );
      }
    }
  };

  const { isFading, isOpen, initializePopUpState, showPopUp } = useActionTimers({
    fadeTimer: fadePlanTimerRef,
    hideTimer: hidePlanTimerRef,
    endTimerHandler: refreshPlanList,
  });

  const handlePlanFavoriteButtonClick = useCallback(
    async (plan: PlanListWithArrayIndex) => {
      initializePopUpState();
      await dispatch(removeFavoriteToPlan(plan));
      showPopUp();
      setRemoveItemCount((prev) => prev + 1);
    },
    [initializePopUpState, dispatch, showPopUp],
  );

  const onLoadNextPage = () => {
    dispatch(
      fetchMorePlanList({
        serviceType: isDomestic ? ServiceType.DOMESTIC : ServiceType.OVERSEAS_HOTEL,
        offset: offset + limit - removeItemCount,
        limit: limit,
        filters: getRequestFilter(query),
        sortOrder: getRequestSort(false, query),
        adultNum: 1,
        roomNum: 1,
      }),
    );
    setRemoveItemCount(0);
  };

  const onClickPaginationButton = (offset: number, _limit: number) => {
    const path = isDomestic ? paths.domesticPlan.path : paths.overseasPlan.path;
    const queryString = getQueryString(query.f_sort || '', getFilterObject(query), offset);

    dispatch(
      pushLocation({
        pathname: path,
        search: queryString,
      }),
    );
  };

  const handleFavoriteRollbackClick = useCallback(async () => {
    initializePopUpState();
    await dispatch(addFavoriteToPlan(isDomestic)).catch(showPopUp);
    setRemoveItemCount((prev) => prev - 1);
  }, [initializePopUpState, dispatch, isDomestic, showPopUp]);

  const isPlanListEmpty = isEmptyArray(
    body?.providerPlanList?.map((providerPlan) => providerPlan.planList).flat(),
  );

  return (
    <>
      {isPlanFetching || (isCurrentPageDataEmpty && isOpen && !isSP && !isTotalDataEmpty) ? (
        <AccommodationPlanSkeleton skeletonItemCount={3} itemPerGroup={2} isPlanList={isPlanList} />
      ) : isPlanListEmpty ? (
        <EmptyMessage isPlanList={isPlanList} status={status} statusMsg={statusMsg} />
      ) : (
        body?.providerPlanList.map((providerPlan, index) => {
          const { provider, planList } = providerPlan;
          return (
            <>
              {!isEmptyArray(planList) && (
                <>
                  <span className={styles.hotelInformationBorder} />
                  <div className={styles.hotelInformationContainer} key={index}>
                    <div className={styles.hotelCardContainer}>
                      <ProviderInformationCard provider={provider} />
                      {planList.map((plan, index) => {
                        const planWithIndex = { ...plan, arrayIndex: index };
                        return (
                          <PlanDetailInformationCard
                            plan={planWithIndex}
                            provider={provider}
                            key={index}
                            handlePlanFavoriteButtonClick={handlePlanFavoriteButtonClick}
                            isDomestic={isDomestic}
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </>
          );
        })
      )}

      {total > 0 ? (
        <Pagination
          offset={body?.offset}
          total={total}
          limit={body?.limit}
          onClickPaginationButton={onClickPaginationButton}
          onLoadNextPage={onLoadNextPage}
          isFetching={isPlanFetchingMore}
          isPlanList={isPlanList}
        />
      ) : null}

      <Animation isStartAction={isOpen} type="fadeIn" animationDuration="short">
        <Popup
          isFade={isFading}
          clickHandler={handleFavoriteRollbackClick}
          label="お気に入りから削除しました。"
        />
      </Animation>
    </>
  );
};

export default AccommodationPlanList;
